import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {
    HttpClient,
    HttpRequest,
    HttpHeaders,
    HttpParams,
    HttpResponse,
} from '@angular/common/http';
import {catchError, filter} from 'rxjs/operators';
import {XlsxResponseModel} from '../models';
import {AdalService} from 'adal-angular4';

@Injectable()
export class HttpService {
    // TODO: change AdalHttp with http service
    constructor(
        private service: AdalService,
        private http: HttpClient
    ) {
    }

    private formatErrors(error: any) {
        return throwError(error);
    }

    public get(url: string): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http.get(url, options).pipe(
            catchError((err) => {
                return this.formatErrors(err);
            })
        );
    }

    public getWithParams(url: string, parametrs: HttpParams): Observable<any> {
        const options = this.prepareOptions('applications/json');
        return this.http.get(url, {
            headers: options.headers,
            observe: 'response',
            params: parametrs,
        });
    }

    public getCSV(url: string, parametrs: HttpParams): Observable<any> {
        const options = this.prepareOptions('text');
        // have to use httpService
        // because http does not allow to change responseType to 'text
        return this.http.get(url, {
            headers: options.headers,
            observe: 'response',
            params: parametrs,
            responseType: 'text',
        });
    }

    getXlsx(url: string, parametrs: HttpParams): Observable<XlsxResponseModel> {
        return Observable.create((observer) => {
            const xhr = new XMLHttpRequest();

            xhr.open('GET', `${url}?${parametrs}`, true);
            xhr.setRequestHeader('Content-type', 'application/json');
            xhr.setRequestHeader(
                'Authorization',
                `Bearer ${this.service.userInfo.token}`
            );
            xhr.responseType = 'blob';

            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        const titleHeader =
                            xhr.getResponseHeader('content-disposition') ||
                            'simulation';
                        const contentType =
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                        const blob = new Blob([xhr.response], {
                            type: contentType,
                        });
                        observer.next({blob, titleHeader});
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };
            xhr.send();
        });
    }

    public post(url: string, body: Object): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http
            .post(url, JSON.stringify(body), options)
            .pipe(catchError((err) => throwError(err)));
    }

    public patch(url: string, body: Object): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http
            .patch(url, JSON.stringify(body), options)
            .pipe(catchError((err) => throwError(err)));
    }

    // pipe is needed because more than one request is emited
    public postFormData(url: string, body: Object): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers
            .set('Accept', 'application/json')
            .set('Authorization', `Bearer ${this.service.userInfo.token}`);

        const uploadReq = new HttpRequest('POST', url, body, {
            headers: headers,
            reportProgress: true,
        });
        return this.http
            .request(uploadReq)
            .pipe(filter((response) => response instanceof HttpResponse));
    }

    public postWithParamsAndBody(url: string, parametrs: HttpParams, body: {}): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers
            .set('Authorization', `Bearer ${this.service.userInfo.token}`);

        return this.http.post(url, body, {
            headers: headers,
            observe: 'response',
            params: parametrs,
        });
    }

    public put(url: string, body: Object): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http
            .put(url, JSON.stringify(body), options)
            .pipe(catchError(this.formatErrors));
    }

    public delete(url: string): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http
            .delete(url, options)
            .pipe(catchError((err) => throwError(err)));
    }

    public deleteWithBody(url: string, body: any): Observable<any> {
        const options = this.prepareOptions('application/json');
        return this.http
            .delete(url, {body: body, ...options})
            .pipe(catchError((err) => throwError(err)));
    }

    public getType(url: string, type: string): Observable<any> {
        const options = this.prepareOptions(type);
        return this.http.get(url, options);
    }

    public getBlob(url: string, contentType: string): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers
            .set('Accept', contentType)
            .set('Authorization', `Bearer ${this.service.userInfo.token}`);
        const options: any = {headers};
        options.responseType = 'blob';
        return this.http.get(url, options);
    }

    private prepareOptions(type: string): any {
        let headers = new HttpHeaders();
        headers = headers
            .set('Accept', type)
            .set('Content-Type', type)
            .set('Authorization', `Bearer ${this.service.userInfo.token}`);
        return {headers};
    }
}
